import Button from '@/src/components/atom/Button';
import LucideIcon from '@/src/components/atom/Icons/LucideIcon';
import Input from '@/src/components/atom/Input';
import RadioGroup from '@/src/components/atom/RadioGroup';
import BottomFixedContainer from '@/src/components/molecule/BottomFixedContainer';
import FormContent from '@/src/components/molecule/FormContent';
import MainContentTitle from '@/src/components/molecule/MainContentTitle';
import SectionCard from '@/src/components/molecule/SectionCard';
import { Form, FormField } from '@/src/components/ui/form';
import { BUTTON_CONST } from '@/src/constants/buttons';
import { TOAST_CONST } from '@/src/constants/toast';
import { useToast } from '@/src/hooks/useToast';
import PATH from '@/src/routes/path';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useController, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { NAV_CONST } from '@/src/constants/nav';
import Select from '@/src/components/atom/Select';
import TextArea from '@/src/components/atom/TextArea';
import { usePrompt } from '@/src/hooks/usePrompt';
import { PutMagazineRequestBody } from '@/src/apis/rest/magazine.dtos';
import { getMagazineByIdAPI, putMagazineAPI } from '@/src/apis/rest/magazine';
import { getCreditTemplateAPI } from '@/src/apis/rest/creditTemplate';
import DateRangePicker from '@/src/components/atom/DateRangePicker';
import dayjs from 'dayjs';
import { DateRange } from 'react-day-picker';
import ImageUpload from '@/src/components/atom/ImageUpload';
import IconButton from '@/src/components/atom/Button/IconButton';
import { inAppLinkRegex, urlRegex } from '@/src/utils/regex';
import SectionLabel from '@/src/components/molecule/SectionLabel';
import Editor from '@/src/components/atom/Editor';
import { Media } from '@/src/models/MeidaDto';
import React, { useEffect, useRef, useState } from 'react';
import useModalState from '@/src/hooks/useModalState';
import AlertDialog from '@/src/components/atom/AlertDialog';
import MagazinePreview from '@/src/pages/Magazine/components/MagazinePreview';
import { webviewDataInjection } from '@/src/helpers/webviewHelper';
import { UNIT_STRINGS } from '@/src/constants/unit';
import ImageUploadInfo from '@/components/molecule/ImageUploadInfo';
import {
  MagazineWebviewMapper,
  MagazineWebviewTemplate,
} from '@/src/helpers/webviewHelper/magazine';
import { getContentTemplatesAPI } from '@/src/apis/rest/contentTemplate';
import { APIError } from '@/src/models/common/apiError';
import { COMMON_ALERT_DIALOG_CONST } from '@/src/constants/alertDialog';
import { OPTIONS_CONST } from '@/src/constants/options';
import { formatter } from '@/src/utils/formatter';
import useDialog, { DialogType } from '@/src/hooks/useDialog';

type ModalState = 'NONE' | 'DELETE' | 'PRIORITY_ERROR' | 'BANNER_CONNECT_ERROR';

const BACK_PATH = PATH.MAGAZINE_DETAIL;

const magazineFormSchema = () => {
  return z
    .object({
      magazineType: z.string(),
      postedAt: z.object({
        from: z.date(),
        to: z.date(),
      }),
      isExposed: z.boolean(),
      priorityDisplayYn: z.boolean(),
      awardedCreditYn: z.boolean(),
      creditTemplateId: z.string().optional().nullable(),
      thumbnailImageId: z.number(),
      title: z.string().min(1),
      subTitle: z.string().optional().nullable(),
      contents: z.string().min(1),
      sponsorImageId: z.number().optional().nullable(),
      sponsorDescription: z.string().optional().nullable(),
      sponsorPageLink: z.string().regex(urlRegex).optional().nullable(),
      bottomButtonDisplayYn: z.boolean(),
      bottomButtonName: z.string().optional().nullable(),
      bottomButtonLink: z.union([
        z.string().regex(urlRegex).optional().nullable(),
        z.string().regex(inAppLinkRegex).optional().nullable(),
      ]),
      magazinePostStartedAt: z.string(),
      magazinePostEndedAt: z.string(),
    })
    .refine(data => {
      if (data.awardedCreditYn) {
        return !!data.creditTemplateId;
      }

      return true;
    })
    .refine(data => {
      if (data.bottomButtonDisplayYn) {
        return data.bottomButtonLink && data.bottomButtonName;
      }

      return true;
    });
};

const DEFAULT_VALUES: Partial<
  PutMagazineRequestBody & { postedAt: DateRange }
> = {
  magazineType: 'common',
  postedAt: { from: undefined, to: undefined },
  isExposed: true,
  priorityDisplayYn: true,
  awardedCreditYn: true,
  bottomButtonDisplayYn: false,
  title: '',
  subTitle: '',
  contents: '',
};

export default function MagazineEditPage() {
  // util
  const navigate = useNavigate();
  const { toast } = useToast();
  const { id } = useParams();
  const { modalState, handleOpenModal, handleCloseModal, handleToggleModal } =
    useModalState<ModalState>();

  // state
  const [defaultData, setDefaultData] = useState('');
  const [isPorceReplace] = useState(false);
  const [isSponsorLinkError, setIsSponsorLinkError] = useState(false);
  const [isBottomLinkError, setIsBottomLinkError] = useState(false);
  const {
    Dialog: EditDialog,
    isOpen: isEditOpen,
    openDialog: openEditDialog,
    closeDialog: closeEditDialog,
  } = useDialog({
    type: DialogType.ALERT,
  });
  const formRef = useRef<HTMLFormElement>(null);

  // form
  const form = useForm<
    PutMagazineRequestBody & {
      postedAt: DateRange;
      thumbnailImage?: Media;
      sponsorImage?: Media;
    }
  >({
    resolver: zodResolver(magazineFormSchema()),
    defaultValues: DEFAULT_VALUES,
    reValidateMode: 'onSubmit',
  });
  const { field: bottomButtonNameField } = useController({
    name: 'bottomButtonName',
    control: form.control,
  });
  const { field: bottomButtonLinkField } = useController({
    name: 'bottomButtonLink',
    control: form.control,
  });
  const { field: thumbnailImageField } = useController({
    name: 'thumbnailImage',
    control: form.control,
  });
  const { field: sponsorImageField } = useController({
    name: 'sponsorImage',
    control: form.control,
  });

  // query
  const { data: creditTemplateOptions } = useQuery({
    queryKey: ['credit-template-options'],
    queryFn: () =>
      getCreditTemplateAPI({
        page: 1,
        pageSize: 9999,
        category: '매거진',
        isActive: true,
      }),
    select: data =>
      (data?.rows || []).map(item => ({
        label: `[매거진] ${String(item?.title || '')} (${Number(
          item?.credit || 0,
        ).toLocaleString()}${UNIT_STRINGS.CREDIT})`,
        value: String(item?.id || ''),
      })),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchIntervalInBackground: false,
  });

  const { data: magazineData } = useQuery({
    queryKey: ['magazine', id],
    queryFn: () => getMagazineByIdAPI({ id: Number(id) }),
    meta: {
      backUrl: BACK_PATH,
      callback: () => {
        navigate(BACK_PATH.replace(':id', String(id)), { replace: true });
      },
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchIntervalInBackground: false,
  });
  const { data: contentTemplateData } = useQuery({
    queryKey: ['content-template', 'MAGAZINE'],
    queryFn: () => {
      return getContentTemplatesAPI({ type: 'MAGAZINE' });
    },
  });
  const contentsMarkup = webviewDataInjection<MagazineWebviewTemplate>(
    contentTemplateData?.rows?.[0]?.markup || '',
    MagazineWebviewMapper({ ...form.watch() }),
  );

  const hasOptionalInfo =
    !!magazineData?.bottomButtonDisplayYn ||
    !!magazineData?.sponsorImage ||
    !!magazineData?.sponsorDescription ||
    !!magazineData?.sponsorPageLink ||
    false;

  // mutation
  const putMagazineMutation = useMutation({ mutationFn: putMagazineAPI });

  // event
  const handleGoBack = () => {
    navigate(-1);
  };

  const handleSubmit = (
    values: PutMagazineRequestBody & { postedAt?: DateRange },
  ) => {
    const newValues = {
      ...values,
      id: Number(id),
      creditTemplateId: values.creditTemplateId
        ? Number(values.creditTemplateId)
        : null,
      subTitle: values?.subTitle || '',
      sponsorImageId: values?.sponsorImageId || null,
      sponsorDescription: values?.sponsorDescription || '',
      sponsorPageLink: values?.sponsorPageLink || '',
      bottomButtonName: values?.bottomButtonName || '',
      bottomButtonLink: values?.bottomButtonLink || '',
    };

    delete newValues.postedAt;

    putMagazineMutation.mutate(newValues, {
      onSuccess: () => {
        toast({
          variant: 'success',
          title: TOAST_CONST.saveSuccess.title,
          description: TOAST_CONST.saveSuccess.description,
        });

        navigate(BACK_PATH.replace(':id', String(id)));
      },
      onError: e => {
        const { error } = e as unknown as APIError;

        if (error === 'CANNOT_PRIORITY_EXPOSE_OVER_THAN_FIVE') {
          handleOpenModal('PRIORITY_ERROR');
        } else {
          toast({
            variant: 'error',
            title: TOAST_CONST.error.title,
            description: e.message,
          });
        }
      },
    });
  };

  // render
  const getDateDisabledMatcher = (date: Date) => {
    if (dayjs().isAfter(date, 'D')) return true;

    return false;
  };
  const renderBottomFixedContent = () => {
    return (
      <BottomFixedContainer>
        <div className="flex justify-between">
          <Button
            className="w-max"
            buttonSize="medium"
            buttonColor="outline"
            onClick={handleGoBack}
          >
            <div className="flex items-center gap-x-2">
              <LucideIcon name="ChevronLeft" size={16} />
              {BUTTON_CONST.backToScreen}
            </div>
          </Button>

          <div className="flex items-center gap-2">
            <Button
              disabled={
                defaultData === JSON.stringify(form.watch()) ||
                !form.formState.isValid
              }
              type="button"
              onClick={openEditDialog}
              className="w-40"
              buttonSize="medium"
              isLoading={putMagazineMutation.isPending}
            >
              <div className="flex items-center gap-x-2">
                {BUTTON_CONST.save}
              </div>
            </Button>
          </div>
        </div>
      </BottomFixedContainer>
    );
  };

  // effect
  useEffect(() => {
    if (magazineData) {
      const resetFormData = {
        ...magazineData,
        postedAt: {
          from: new Date(magazineData?.magazinePostStartedAt),
          to: new Date(magazineData?.magazinePostEndedAt),
        },
        sponsorImageId: magazineData?.sponsorImage?.id,
        thumbnailImageId: magazineData?.thumbnailImage?.id,
        creditTemplateId: magazineData?.creditTemplate?.id
          ? (String(magazineData?.creditTemplate?.id) as unknown as number)
          : undefined,
      };

      form.reset(resetFormData);
      setDefaultData(JSON.stringify(resetFormData));
    }
  }, [form, magazineData]);

  // usePrompt
  usePrompt(
    defaultData !== JSON.stringify(form.watch()) &&
      !form.formState.isSubmitted &&
      !isPorceReplace,
  );
  return (
    <>
      <MainContentTitle
        breadcrumbList={[NAV_CONST.magazineManagement, NAV_CONST.magazineEdit]}
        pageTitle={NAV_CONST.magazineEdit}
      />
      {magazineData && (
        <Form {...form}>
          <form
            ref={formRef}
            className="relative mb-[100px] flex flex-col gap-6"
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <SectionCard cardTitle="매거진 기본 정보">
              <div className="grid grid-cols-[1fr_auto_1fr] gap-x-6 gap-y-4">
                <div className="flex flex-col gap-6">
                  <FormField
                    control={form.control}
                    name="magazineType"
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <FormContent label="매거진 유형" error={error}>
                          <RadioGroup
                            optionList={[
                              {
                                label: OPTIONS_CONST.notAdvertisement,
                                value: 'common',
                              },
                              {
                                label: OPTIONS_CONST.advertisement,
                                value: 'advertising',
                              },
                            ]}
                            rootProps={{
                              value: field.value,
                              onValueChange: field.onChange,
                            }}
                          />
                        </FormContent>
                      );
                    }}
                  />
                  <FormField
                    control={form.control}
                    name="postedAt"
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <FormContent isRequired label="게시 기간" error={error}>
                          <DateRangePicker
                            {...field}
                            onChange={e => {
                              field.onChange(e);

                              form.setValue(
                                'magazinePostStartedAt',
                                e?.from
                                  ? dayjs(e?.from).format('YYYY-MM-DD')
                                  : '',
                              );
                              form.setValue(
                                'magazinePostEndedAt',
                                e?.to ? dayjs(e?.to).format('YYYY-MM-DD') : '',
                              );
                            }}
                            dateDisabledMatcher={getDateDisabledMatcher}
                          />
                        </FormContent>
                      );
                    }}
                  />
                  <FormField
                    control={form.control}
                    name="isExposed"
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <FormContent label="노출 여부" error={error}>
                          <RadioGroup
                            optionList={[
                              { label: OPTIONS_CONST.exposed, value: 'true' },
                              {
                                label: OPTIONS_CONST.notExposed,
                                value: 'false',
                              },
                            ]}
                            rootProps={{
                              value: field.value ? 'true' : 'false',
                              onValueChange: e => field.onChange(e === 'true'),
                            }}
                          />
                        </FormContent>
                      );
                    }}
                  />
                  <FormField
                    control={form.control}
                    name="priorityDisplayYn"
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <FormContent label="우선 노출 여부" error={error}>
                          <RadioGroup
                            optionList={[
                              { label: OPTIONS_CONST.applied, value: 'true' },
                              {
                                label: OPTIONS_CONST.notApplied,
                                value: 'false',
                              },
                            ]}
                            rootProps={{
                              value: field.value ? 'true' : 'false',
                              onValueChange: e => field.onChange(e === 'true'),
                            }}
                          />
                        </FormContent>
                      );
                    }}
                  />
                  <FormField
                    control={form.control}
                    name="thumbnailImageId"
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <FormContent
                          isRequired
                          label="썸네일 이미지"
                          defaultMessage={
                            <ImageUploadInfo widthSize={319} heightSize={480} />
                          }
                          error={error}
                        >
                          <ImageUpload
                            width={128}
                            height={195}
                            widthText={319}
                            heightText={480}
                            maxImageSizeMb={5}
                            imageClassName="object-cover"
                            value={thumbnailImageField?.value?.bufferFullUrl}
                            onChange={e => {
                              field.onChange(e?.id);
                              thumbnailImageField.onChange(e);
                            }}
                            topAccessary={({ handleEdit, handleRemove }) => {
                              return (
                                <>
                                  <div className="flex gap-x-1">
                                    <IconButton
                                      buttonSize="xSmall"
                                      buttonColor="outline"
                                      lucideIconProps={{ name: 'Pencil' }}
                                      onClick={() => {
                                        handleEdit();
                                      }}
                                    />
                                    <IconButton
                                      buttonSize="xSmall"
                                      buttonColor="outline"
                                      lucideIconProps={{ name: 'X' }}
                                      onClick={() => {
                                        handleRemove();
                                        field.onChange(undefined);
                                        thumbnailImageField.onChange(undefined);
                                      }}
                                    />
                                  </div>
                                </>
                              );
                            }}
                          />
                        </FormContent>
                      );
                    }}
                  />
                </div>
                <div className="border-r border-r-gray1" />
                <div className="flex flex-col gap-6">
                  <FormField
                    control={form.control}
                    name="awardedCreditYn"
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <FormContent label="크레딧 지급 여부" error={error}>
                          <RadioGroup
                            optionList={[
                              { label: OPTIONS_CONST.awarded, value: 'true' },
                              {
                                label: OPTIONS_CONST.notAwarded,
                                value: 'false',
                              },
                            ]}
                            rootProps={{
                              value: field.value ? 'true' : 'false',
                              onValueChange: e => {
                                if (e === 'false') {
                                  form.setValue('creditTemplateId', undefined);
                                }
                                field.onChange(e === 'true');
                              },
                            }}
                          />
                        </FormContent>
                      );
                    }}
                  />
                  <FormField
                    control={form.control}
                    name="creditTemplateId"
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <FormContent label="지급 크레딧" error={error}>
                          {defaultData && creditTemplateOptions ? (
                            <Select
                              option={[
                                {
                                  optionList: creditTemplateOptions,
                                },
                              ]}
                              valueProps={{
                                placeholder: '크레딧 카테고리 선택',
                              }}
                              rootProps={{
                                value: field.value
                                  ? String(field.value)
                                  : undefined,
                                onValueChange: e => field.onChange(e),
                                disabled: !form.watch('awardedCreditYn'),
                              }}
                            />
                          ) : (
                            <div className="h-[40px]" />
                          )}
                        </FormContent>
                      );
                    }}
                  />
                  <SectionLabel
                    labelProps={{ className: 'translate-y-2' }}
                    label="조회 수"
                    value={
                      <Input
                        containerClassName="flex-1"
                        value={magazineData?.magazineViewHistoryCount}
                        disabled
                      />
                    }
                  />
                  <SectionLabel
                    labelProps={{ className: 'translate-y-2' }}
                    label="북마크 수"
                    value={
                      <Input
                        containerClassName="flex-1"
                        value={magazineData?.magazineBookmarkCount}
                        disabled
                      />
                    }
                  />
                  <SectionLabel
                    labelProps={{ className: 'translate-y-2' }}
                    label="등록 일시"
                    value={
                      <Input
                        containerClassName="flex-1"
                        value={formatter.DATE(magazineData?.createdAt)}
                        disabled
                      />
                    }
                  />
                  <SectionLabel
                    labelProps={{ className: 'translate-y-2' }}
                    label="등록 관리자"
                    value={
                      <Input
                        containerClassName="flex-1"
                        value={magazineData?.administrator?.name}
                        disabled
                      />
                    }
                  />
                </div>
              </div>
            </SectionCard>
            <SectionCard
              cardTitle="매거진 선택 정보"
              isCollapsible
              defaultOpen={hasOptionalInfo}
            >
              <div className="grid grid-cols-[1fr_auto_1fr] gap-x-6 gap-y-4">
                <div className="flex flex-col gap-6">
                  <FormField
                    control={form.control}
                    name="sponsorImageId"
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <FormContent
                          label="제휴사 이미지"
                          defaultMessage={
                            <ImageUploadInfo widthSize={335} heightSize={335} />
                          }
                          error={error}
                        >
                          <ImageUpload
                            width={128}
                            height={128}
                            widthText={335}
                            heightText={335}
                            maxImageSizeMb={5}
                            imageClassName="object-cover"
                            value={sponsorImageField?.value?.bufferFullUrl}
                            onChange={e => {
                              field.onChange(e?.id);
                              sponsorImageField.onChange(e);
                            }}
                            topAccessary={({ handleEdit, handleRemove }) => {
                              return (
                                <>
                                  <div className="flex gap-x-1">
                                    <IconButton
                                      buttonSize="xSmall"
                                      buttonColor="outline"
                                      lucideIconProps={{ name: 'Pencil' }}
                                      onClick={() => {
                                        handleEdit();
                                      }}
                                    />
                                    <IconButton
                                      buttonSize="xSmall"
                                      buttonColor="outline"
                                      lucideIconProps={{ name: 'X' }}
                                      onClick={() => {
                                        handleRemove();
                                        field.onChange(undefined);
                                        sponsorImageField.onChange(undefined);
                                      }}
                                    />
                                  </div>
                                </>
                              );
                            }}
                          />
                        </FormContent>
                      );
                    }}
                  />
                  <FormField
                    control={form.control}
                    name="sponsorDescription"
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <FormContent label="제휴사 설명" error={error}>
                          <TextArea
                            className="h-[100px]"
                            placeholder="제휴사에 대해 간략한 설명을 적을 수 있어요"
                            {...field}
                          />
                        </FormContent>
                      );
                    }}
                  />
                  <FormField
                    control={form.control}
                    name="sponsorPageLink"
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <FormContent
                          label="제휴사 홈페이지 링크"
                          error={
                            isSponsorLinkError
                              ? {
                                  type: 'pattern',
                                  message:
                                    'url 형식을 확인해주세요 (https://example.com)',
                                }
                              : undefined
                          }
                        >
                          <Input
                            value={field?.value}
                            placeholder="링크를 입력해주세요 (https://example.com)"
                            onChange={e => {
                              field.onChange(e.target.value || undefined);
                              if (e.target.value) {
                                setIsSponsorLinkError(
                                  !urlRegex.test(e.target.value),
                                );
                              } else {
                                setIsSponsorLinkError(false);
                              }
                            }}
                            onClear={() => {
                              field.onChange('');
                              setIsSponsorLinkError(false);
                              setTimeout(() => {
                                field.onChange(undefined);
                              }, 0);
                            }}
                          />
                        </FormContent>
                      );
                    }}
                  />
                </div>
                <div className="border-r border-r-gray1" />
                <div className="flex flex-col gap-6">
                  <FormField
                    control={form.control}
                    name="bottomButtonDisplayYn"
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <FormContent label="하단 버튼 노출 여부" error={error}>
                          <RadioGroup
                            optionList={[
                              { label: '선택 안함', value: 'false' },
                              { label: '버튼 생성', value: 'true' },
                            ]}
                            rootProps={{
                              value: field.value ? 'true' : 'false',
                              onValueChange: e => {
                                if (e === 'false') {
                                  bottomButtonNameField.onChange('');
                                  bottomButtonLinkField.onChange('');
                                  setTimeout(() => {
                                    bottomButtonNameField.onChange(undefined);
                                    bottomButtonLinkField.onChange(undefined);
                                  }, 0);
                                }
                                field.onChange(e === 'true');
                              },
                            }}
                          />
                        </FormContent>
                      );
                    }}
                  />
                  <FormField
                    control={form.control}
                    name="bottomButtonName"
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <FormContent label="하단 버튼 명" error={error}>
                          <Input
                            maxLength={20}
                            disabled={!form.watch('bottomButtonDisplayYn')}
                            value={field?.value}
                            onChange={e => {
                              field.onChange(e.target.value);
                            }}
                            placeholder="하단 버튼 이름을 입력해주세요 (최대 20글자)"
                            onClear={() => {
                              field.onChange('');
                            }}
                          />
                        </FormContent>
                      );
                    }}
                  />
                  <FormField
                    control={form.control}
                    name="bottomButtonLink"
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <FormContent
                          label="하단 버튼 링크"
                          error={
                            isBottomLinkError
                              ? {
                                  type: 'pattern',
                                  message:
                                    '하단 버튼 링크 형식을 확인해주세요 (https://example.com 또는 /app/*)',
                                }
                              : undefined
                          }
                        >
                          <Input
                            disabled={!form.watch('bottomButtonDisplayYn')}
                            value={field?.value}
                            onChange={e => {
                              field.onChange(e.target.value || undefined);
                              if (e.target.value) {
                                setIsBottomLinkError(
                                  !urlRegex.test(e.target.value) &&
                                    !inAppLinkRegex.test(e.target.value),
                                );
                              } else {
                                setIsBottomLinkError(false);
                              }
                            }}
                            placeholder="링크를 입력해주세요 (https://example.com 또는 /app/*)"
                            onClear={() => {
                              field.onChange('');
                              setIsBottomLinkError(false);
                              setTimeout(() => {
                                field.onChange(undefined);
                              }, 0);
                            }}
                          />
                        </FormContent>
                      );
                    }}
                  />
                </div>
              </div>
            </SectionCard>
            <SectionCard cardTitle="매거진 내용">
              <div className="flex gap-6">
                <div className="flex flex-1 flex-col gap-6">
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <FormContent isRequired label="제목" error={error}>
                          <Input
                            value={field?.value}
                            onChange={e => {
                              field.onChange(e.target.value);
                            }}
                            placeholder="제목을 입력해주세요"
                            onClear={() => {
                              field.onChange('');
                            }}
                          />
                        </FormContent>
                      );
                    }}
                  />{' '}
                  <FormField
                    control={form.control}
                    name="subTitle"
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <FormContent label="서브 제목" error={error}>
                          <Input
                            value={field?.value}
                            onChange={e => {
                              field.onChange(e.target.value);
                            }}
                            placeholder="서브 제목을 입력해주세요"
                            onClear={() => {
                              field.onChange('');
                            }}
                          />
                        </FormContent>
                      );
                    }}
                  />
                  <FormField
                    control={form.control}
                    name="contents"
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <FormContent
                          className="col-span-3"
                          label="내용"
                          isRequired
                          error={error}
                        >
                          <div className="flex gap-3">
                            <div className="flex-1">
                              <Editor
                                placeholder="내용을 입력해주세요"
                                height={650}
                                value={field.value || ''}
                                onChange={field.onChange}
                              />
                            </div>
                          </div>
                        </FormContent>
                      );
                    }}
                  />
                </div>
                <MagazinePreview contentsMarkup={contentsMarkup} />
              </div>
            </SectionCard>
            {renderBottomFixedContent()}
          </form>
        </Form>
      )}
      {/* 모달 */}
      {modalState === 'PRIORITY_ERROR' && (
        <AlertDialog
          open={modalState === 'PRIORITY_ERROR'}
          onOpenChange={() => handleToggleModal('PRIORITY_ERROR')}
          okText={BUTTON_CONST.ok}
          onOk={handleCloseModal}
          title="우선 노출 여부 지정 불가"
          cancelButtonProps={{
            className: 'hidden',
          }}
        >
          <p className="whitespace-pre-wrap">
            이미 해당 노출 기간 내 우선노출 지정 개수가 5개 있습니다.
            <br />
            다른 매거진을 확인 후 재시도 해주세요.
          </p>
        </AlertDialog>
      )}
      {modalState === 'BANNER_CONNECT_ERROR' && (
        <AlertDialog
          open={modalState === 'BANNER_CONNECT_ERROR'}
          onOpenChange={() => handleToggleModal('BANNER_CONNECT_ERROR')}
          okText={BUTTON_CONST.ok}
          onOk={handleCloseModal}
          title="삭제 불가"
          cancelButtonProps={{
            className: 'hidden',
          }}
        >
          <p className="whitespace-pre-wrap">
            연결된 배너가 있어서 삭제가 불가능합니다.
            <br />
            배너를 확인해주세요.
          </p>
        </AlertDialog>
      )}
      {isEditOpen && (
        <EditDialog
          open={isEditOpen}
          onOk={() => formRef?.current?.requestSubmit()}
          onOpenChange={e => {
            if (e) {
              openEditDialog();
            } else {
              closeEditDialog();
            }
          }}
          title={COMMON_ALERT_DIALOG_CONST.edit}
          isLoading={putMagazineMutation.isPending}
        />
      )}
    </>
  );
}
